"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";

/**
 * Botão flutuante de atendimento via WhatsApp.
 * - Número vem de /api/website/public/logos (tb_company.comp_cell — Metadados da Empresa)
 * - Animação pulsante para chamar atenção
 * - Posição: mais baixa em telas grandes (desktop), próximo do rodapé em mobile
 */
export default function LiveChat() {
  const pathname = usePathname();
  const [companyName, setCompanyName] = useState<string>("");
  const [whatsappNumber, setWhatsappNumber] = useState<string>("");

  // Ocultar na página de assinatura de lista de presença
  if (pathname?.startsWith("/treinamento/lista/")) {
    return null;
  }

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || "http://localhost:5000";
        const response = await fetch(`${backendUrl}/api/website/public/logos`);
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            if (data.data?.company_name)     setCompanyName(data.data.company_name);
            if (data.data?.company_whatsapp) setWhatsappNumber(data.data.company_whatsapp);
          }
        }
      } catch {
        // silent fail
      }
    };
    fetchCompanyData();
  }, []);

  // Normaliza número (remove tudo que não é dígito).
  // Se vier sem o código do país (55), prefixa.
  const normalizePhone = (raw: string): string => {
    const digits = (raw || "").replace(/\D/g, "");
    if (!digits) return "";
    return digits.startsWith("55") ? digits : `55${digits}`;
  };

  const phoneNumber = normalizePhone(whatsappNumber);
  if (!phoneNumber) return null; // sem número configurado, não mostra o botão

  const defaultMessage =
    `Olá${companyName ? " " + companyName : ""}! Vim do site e gostaria de mais informações sobre os treinamentos.`;
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(defaultMessage)}`;

  return (
    <>
      {/* Keyframes da animação pulsante (anel verde se expandindo ao redor) */}
      <style jsx>{`
        @keyframes whatsapp-pulse {
          0%   { transform: scale(1);   opacity: 0.7; }
          70%  { transform: scale(1.6); opacity: 0;   }
          100% { transform: scale(1.6); opacity: 0;   }
        }
        .pulse-ring {
          position: absolute;
          inset: 0;
          border-radius: 9999px;
          background-color: #25D366;
          animation: whatsapp-pulse 1.8s ease-out infinite;
          z-index: 0;
        }
      `}</style>

      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Atendimento via WhatsApp"
        title="Fale conosco pelo WhatsApp"
        className="fixed bottom-24 md:bottom-6 right-6 w-14 h-14 md:w-16 md:h-16 rounded-full shadow-lg flex items-center justify-center transition-transform duration-200 z-50 hover:scale-110"
        style={{ backgroundColor: "#25D366" }}
      >
        {/* Anel pulsante de fundo */}
        <span className="pulse-ring" aria-hidden="true"></span>

        {/* Logo oficial WhatsApp */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          className="w-8 h-8 md:w-9 md:h-9 relative z-10"
          fill="white"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </a>
    </>
  );
}
